// Mock data generators for the Employee Profile screen — keyed by employee so the demo
// data stays stable across renders. TODO: replace with real API calls once available
// (fetchEmployeeProfile / fetchCoachingHistory / fetchActivities / fetchEmployeeOnboardingPlan).
import { MyTeamMember } from '../types';
import { WeeklyMetricRow, WeeklyMetricCell } from '../BeginActivity/common/types/beginActivity.common.types';
import {
  EmployeeProfileDetails,
  EmployeeCoachingHistoryRow,
  EmployeeAllHistoryRow,
  EmployeeOnboardingPlan,
} from './types';

export function buildEmployeeProfileDetails(member: MyTeamMember): EmployeeProfileDetails {
  return {
    manager: member.manager || '—',
    startDate: member.hireDate || '—',
    level: member.role.includes('Certifier') ? 'Certifier' : member.role.includes('Checker') ? 'Checker' : 'Associate',
    service: member.seniority || '—',
    organization: `${member.org || 'PBNA'} | ${member.department || 'Warehouse'}`,
    currentPeriod: 'P8 W3',
    location: 'Riverside DC',
    shift: member.shift || '—',
  };
}

function makeCell(wk: number, actual: number, target: number): WeeklyMetricCell {
  return { weekLabel: `W${wk}`, actual, target, state: actual >= target ? 'met' : 'miss' };
}

export function buildEmployeeMetricRows(): WeeklyMetricRow[] {
  return [
    {
      metricName: 'DPS', unit: '%', rollingAverage: 0.979, weeksMet: 3, weeksTotal: 12,
      cells: [
        makeCell(13, 0.921, 1.0), makeCell(14, 0.953, 1.0), makeCell(15, 0.976, 1.0), makeCell(16, 0.948, 1.0),
        makeCell(17, 0.987, 1.0), makeCell(18, 0.992, 1.0), makeCell(19, 0.963, 1.0), makeCell(20, 1.002, 1.0),
        makeCell(21, 1.014, 1.0), makeCell(22, 0.986, 1.0), makeCell(23, 1.031, 1.0),
      ],
    },
    {
      metricName: 'KU', unit: '%', rollingAverage: 0.931, weeksMet: 2, weeksTotal: 12,
      cells: [
        makeCell(13, 0.91, 0.95), makeCell(14, 0.918, 0.95), makeCell(15, 0.922, 0.95), makeCell(16, 0.914, 0.95),
        makeCell(17, 0.931, 0.95), makeCell(18, 0.936, 0.95), makeCell(19, 0.923, 0.95), makeCell(20, 0.928, 0.95),
        makeCell(21, 0.939, 0.95), makeCell(22, 0.941, 0.95), makeCell(23, 0.952, 0.95),
      ],
    },
    {
      metricName: 'Gap Time', unit: 'sec', rollingAverage: 5.1, weeksMet: 6, weeksTotal: 12,
      cells: [
        makeCell(13, 5.8, 5.0), makeCell(14, 5.5, 5.0), makeCell(15, 5.3, 5.0), makeCell(16, 5.4, 5.0),
        makeCell(17, 5.1, 5.0), makeCell(18, 4.9, 5.0), makeCell(19, 5.2, 5.0), makeCell(20, 4.8, 5.0),
        makeCell(21, 4.7, 5.0), makeCell(22, 4.9, 5.0), makeCell(23, 4.8, 5.0),
      ],
    },
    {
      metricName: 'BPA', unit: 'ratio', rollingAverage: 1.22, weeksMet: 8, weeksTotal: 12,
      cells: [
        makeCell(13, 1.12, 1.25), makeCell(14, 1.15, 1.25), makeCell(15, 1.18, 1.25), makeCell(16, 1.14, 1.25),
        makeCell(17, 1.22, 1.25), makeCell(18, 1.25, 1.25), makeCell(19, 1.20, 1.25), makeCell(20, 1.23, 1.25),
        makeCell(21, 1.31, 1.25), makeCell(22, 1.29, 1.25), makeCell(23, 1.24, 1.25),
      ],
    },
    {
      metricName: 'Cases Picked', unit: 'cases', rollingAverage: 1182, weeksMet: 9, weeksTotal: 12,
      cells: [
        makeCell(13, 1210, 1150), makeCell(14, 1089, 1150), makeCell(15, 1132, 1150), makeCell(16, 1098, 1150),
        makeCell(17, 1176, 1150), makeCell(18, 1205, 1150), makeCell(19, 1158, 1150), makeCell(20, 1192, 1150),
        makeCell(21, 1248, 1150), makeCell(22, 1276, 1150), makeCell(23, 1214, 1150),
      ],
    },
  ];
}

export function buildEmployeeCoachingHistory(member: MyTeamMember): EmployeeCoachingHistoryRow[] {
  return [
    {
      id: `coach-${member.id}-1`,
      employeeName: member.name,
      actionType: 'Metric',
      focusArea: 'Gap Time',
      actionPlan: 'Stage the replenishment pallets before the wave to hold gap time under 5 minutes.',
      managerSubmitted: member.manager || '—',
      activityDate: '2026-06-09',
      source: 'Observation',
      status: 'Open',
      result: 'Pending',
    },
    {
      id: `coach-${member.id}-2`,
      employeeName: member.name,
      actionType: 'Process',
      focusArea: 'Geo Loading',
      actionPlan: 'Load to the geo sequence on every outbound trailer this period.',
      managerSubmitted: member.manager || '—',
      activityDate: '2026-06-02',
      source: 'Observation',
      status: 'Closed',
      result: 'Complete',
    },
  ];
}

export function buildEmployeeAllHistory(member: MyTeamMember): EmployeeAllHistoryRow[] {
  return [
    {
      id: `hist-${member.id}-1`,
      employeeName: member.name,
      role: member.role,
      type: 'Routine Observation',
      result: 'Completed',
      createdOn: '2026-07-22',
      createdBy: member.manager || '—',
      finalized: '2026-07-22',
      periodWeek: 'P8W2 26',
      status: 'Completed',
      activityId: `act-${member.id}-1`,
    },
    {
      id: `hist-${member.id}-2`,
      employeeName: member.name,
      role: member.role,
      type: 'Training',
      result: 'Completed',
      createdOn: '2026-07-21',
      createdBy: member.manager || '—',
      finalized: '2026-07-21',
      periodWeek: 'P8W2 26',
      status: 'Completed',
      activityId: `act-${member.id}-2`,
    },
  ];
}

export function buildEmployeeOnboardingPlan(member: MyTeamMember): EmployeeOnboardingPlan {
  if (member.status !== 'In Onboarding') {
    return { dayLabel: '', completedCount: 0, totalCount: 0, overdueCount: 0, phases: [], rows: [] };
  }
  const rows: EmployeeOnboardingPlan['rows'] = [
    { id: `ob-${member.id}-1`, phase: 'Week 1', activity: 'New Hire Orientation', type: 'Training', priority: 'Mandatory', due: '2026-07-16', status: 'Done', isImported: true },
    { id: `ob-${member.id}-2`, phase: 'Week 1', activity: 'SRL Training', type: 'Training', priority: 'Mandatory', due: '2026-07-16', status: 'Open', isImported: false },
    { id: `ob-${member.id}-3`, phase: 'Week 1', activity: 'PIT Training', type: 'Training', priority: 'Mandatory', due: '2026-07-16', status: 'Open', isImported: false },
    { id: `ob-${member.id}-4`, phase: 'Week 1', activity: 'Industrial Athlete', type: 'Training', priority: 'Mandatory', due: '2026-07-16', status: 'Done', isImported: true },
    { id: `ob-${member.id}-5`, phase: 'Week 1', activity: 'EHS Compliance', type: 'Certification', priority: 'Mandatory', due: '2026-08-06', status: 'Done', isImported: true },
    { id: `ob-${member.id}-6`, phase: 'Week 2', activity: 'SRL Certification', type: 'Certification', priority: 'Mandatory', due: '2026-08-06', status: 'Done', isImported: true },
  ];
  const phases: EmployeeOnboardingPlan['phases'] = [
    { key: 'Week 1', completed: 3, total: 5, overdue: true, current: false },
    { key: 'Week 2', completed: 2, total: 3, overdue: true, current: false },
    { key: 'Week 3', completed: 0, total: 1, overdue: true, current: false },
    { key: 'Milestone 1', completed: 0, total: 1, overdue: false, current: true },
    { key: 'Milestone 2', completed: 0, total: 1, overdue: false, current: false },
    { key: 'Milestone 3', completed: 0, total: 1, overdue: false, current: false },
  ];
  return {
    dayLabel: 'Day 23 of 84',
    completedCount: 5,
    totalCount: 12,
    overdueCount: 4,
    phases,
    rows,
  };
}
